import React, { useState } from 'react';
import { Divider, Dialog } from '@mui/material';
import DialogHeader from 'common/DialogHeader/DialogHeader';
import { useAppDispatch } from 'store/hooks';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import { useTranslation } from 'react-i18next';
import {
  useAddScheduleMutation,
  schedulesApi,
} from 'store/schedules/schedules';
import { Schedule } from 'store/schedules/interfaces';
import convertScheduleRanges from 'components/SchedulePreviewCalendar/convertScheduleRanges';
import WorkScheduleForm from './WorkScheduleForm/WorkScheduleForm';
import WorkScheduleCalendar from './WorkScheduleCalendar/WorkScheduleCalendar';
import WorkScheduleCalendarProvider from './WorkScheduleCalendar/context/WorkScheduleCalendarProvider';
import { FormPayload } from './WorkScheduleForm/interfaces';

interface AddWorkScheduleProps {
  onClose: () => void;
  isModalOpen: boolean;
}

enum AddWorkScheduleStep {
  form,
  calendar,
}

export default function AddWorkSchedule({
  onClose,
  isModalOpen,
}: AddWorkScheduleProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState(AddWorkScheduleStep.form);
  const [schedule, setSchedule] = useState<Schedule | null>(null);

  const [addSchedule, { isLoading: addScheduleLoading }] =
    useAddScheduleMutation();

  const handleCloseModal = () => {
    onClose();
    if (step === AddWorkScheduleStep.calendar) {
      dispatch(schedulesApi.util.invalidateTags([{ type: 'Schedules' }]));
    }
    setStep(AddWorkScheduleStep.form);
    setSchedule(null);
  };

  const onFormSubmit = async ({ description, ...restPayload }: FormPayload) => {
    try {
      const addedSchedule = await addSchedule({
        ...restPayload,
        ...(description && { description }),
      }).unwrap();
      setSchedule(addedSchedule);
      setStep(AddWorkScheduleStep.calendar);
      dispatch(setSuccessStatus(true));
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  const scheduleRanges = convertScheduleRanges({
    scheduleRanges: schedule?.scheduleRanges ?? [],
    isRegularWeek: schedule?.regular ?? true,
  });

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={step === AddWorkScheduleStep.calendar ? false : 'sm'}
    >
      <DialogHeader
        title={t('project_tracking.add_work_schedule')}
        onClose={handleCloseModal}
      />
      <Divider />
      {step === AddWorkScheduleStep.form && (
        <WorkScheduleForm
          handleBack={handleCloseModal}
          onSubmit={onFormSubmit}
          isSubmitting={addScheduleLoading}
        />
      )}
      {step === AddWorkScheduleStep.calendar && schedule && (
        <WorkScheduleCalendarProvider
          schedule={schedule}
          initWorkSchedules={scheduleRanges}
          onScheduleEdit={(updatedSchedule) => setSchedule(updatedSchedule)}
        >
          <WorkScheduleCalendar
            handleCloseModal={handleCloseModal}
            onScheduleEdit={(updatedSchedule) => setSchedule(updatedSchedule)}
          />
        </WorkScheduleCalendarProvider>
      )}
    </Dialog>
  );
}
